import React, { useRef, useState } from "react";
import { contractsAttributes } from "../../../../interfaces";

interface Props {
  documents: contractsAttributes[];
  label: string;
  renderDocument: (document: contractsAttributes) => React.ReactNode;
}

export const DocumentSlider: React.FC<Props> = ({ documents, label, renderDocument }) => {
  const [selectedId, setSelectedId] = useState("");
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const activeIndex = Math.max(0, documents.findIndex(document => document.id === selectedId));
  if (!documents.length) return null;
  if (documents.length === 1) return <>{renderDocument(documents[0])}</>;

  const select = (index: number) => {
    const next = (index + documents.length) % documents.length;
    setSelectedId(documents[next].id);
  };

  return <div className="expiration-slider" role="group" aria-roledescription="carrossel" aria-label={label}>
    <div className="expiration-slider-viewport" tabIndex={0}
      aria-label="Documento atual. Use as setas para navegar."
      onKeyDown={event => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          select(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
        }
      }}
      onTouchStart={event => {
        const touch = event.touches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
      }}
      onTouchCancel={() => { touchStart.current = null; }}
      onTouchEnd={event => {
        const start = touchStart.current;
        touchStart.current = null;
        const touch = event.changedTouches[0];
        if (!start || !touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) select(activeIndex + (dx < 0 ? 1 : -1));
      }}>
      <div className="expiration-slide" key={documents[activeIndex].id}
        role="group" aria-roledescription="slide" aria-label={`${activeIndex + 1} de ${documents.length}`}>
        {renderDocument(documents[activeIndex])}
      </div>
    </div>
    <div className="expiration-slider-controls">
      {documents.length > 1 && <button type="button" className="expiration-slider-arrow"
        aria-label="Documento anterior" onClick={() => select(activeIndex - 1)}>‹</button>}
      <div className="expiration-slider-dots" role="group" aria-label="Escolher documento">
        {documents.map((document, index) => <button type="button" key={document.id}
          className="expiration-slider-dot" aria-label={`Exibir documento ${index + 1}`}
          aria-current={index === activeIndex ? "true" : undefined}
          title={`#${document.friendly_id || "—"} · ${document.name}`}
          onClick={() => select(index)} />)}
      </div>
      {documents.length > 1 && <button type="button" className="expiration-slider-arrow"
        aria-label="Próximo documento" onClick={() => select(activeIndex + 1)}>›</button>}
    </div>
  </div>;
};
